import IconButton from 'components/IconButton';
import ModalDialog from 'components/ModalDialog';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Fragment, Suspense, useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Collapse,
  Button,
} from 'reactstrap';
import { currentChartTypeAtom } from 'views/state';
import { WAType } from './config';
import CurrentCharts from './currentCharts';
import HistoryCharts from './historyCharts';
import {
  severityFiltersAtom,
  timeSpanAtom,
  intervalAtom,
  showHistoryChartCountsAtom,
  historyChartsOpenAtom,
  currentVulnChartsOpenAtom,
  isGroupingByQidVulnListingOpenAtom,
  hostCountsAtom,
  appliedWAIdsAtom,
} from './state';
import HistoryChartHeader from '../components/HistoryChart/HistoryChartHeader';
import SeverityFilterDropdown from '../components/SeverityFilterDropdown';
import '../styles.css';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import VulnerabilityMetrics from './VulnMetrics';
import WAList from './WAList';
import {
  ChartType,
  DashboardType,
  ModalDialogData,
  TooltipLabelsType,
} from '../types';
import AppliedHostInfo from '../components/AppliedHostInfo';

const WADashboard = () => {
  const [currentChartType, setCurrentChartType] = useAtom(currentChartTypeAtom);
  const setIsGroupingByQidVulnListingOpen = useSetAtom(
    isGroupingByQidVulnListingOpenAtom,
  );

  const hostCounts = useAtomValue(hostCountsAtom);

  // TOGGLE THE COLLAPSE FOR HISTORY CHARTS AND CURRENT CHARTS SECTIONS
  const [historyChartsOpen, setHistoryChartsOpen] = useAtom(
    historyChartsOpenAtom,
  );
  const [currentVulnChartsOpen, setCurrentVulnChartsOpen] = useAtom(
    currentVulnChartsOpenAtom,
  );

  const [modalComponentData, setModalComponentData] =
    useState<ModalDialogData>();

  const listingScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      if (window.location.href.includes(`#${DashboardType.WAS}`)) {
        if (listingScrollRef.current) {
          listingScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 1000);

    return () => clearTimeout(scrollTimeout);
  }, [DashboardType.WAS]);

  function getToolTipLabels() {
    const toolTipLabels: TooltipLabelsType = [];
    const webappsCounts = hostCounts[WAType.Webapp];
    toolTipLabels.push({
      label: WAType.Webapp,
      hostsCount: webappsCounts,
    });

    return toolTipLabels;
  }

  return (
    <Fragment>
      <div className="container-fluid p-0">
        <nav className="navbar navbar-expand-sm bg-white border-top-0 border border-bottom-light sticky-top px-0">
          <div
            className="row w-100 align-items-center"
            style={{ margin: '0px' }}
          >
            {/* First Column: Left-aligned content */}
            <Col xs="12" md="3" lg="6">
              <AppliedHostInfo appliedHostIdsAtom={appliedWAIdsAtom} />
            </Col>

            {/* Second Column: Right-aligned content */}
            <Col xs="12" md="9" lg="6" className="d-flex justify-content-end">
              <div className="d-flex flex-row">
                <SeverityFilterDropdown
                  severityFiltersAtom={severityFiltersAtom}
                />
              </div>
            </Col>
          </div>
        </nav>
        <Card>
          <CardHeader>
            <Row>
              <Col className="col-auto">
                <CardTitle tag="h4">
                  Vulnerabilities Trending History&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() => setHistoryChartsOpen(!historyChartsOpen)}
                  >
                    <i
                      className={
                        historyChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={historyChartsOpen}>
                  <div className="d-inline float-right">
                    <HistoryChartHeader
                      intervalAtom={intervalAtom}
                      showHistoryChartCountsAtom={showHistoryChartCountsAtom}
                      timeSpanAtom={timeSpanAtom}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={historyChartsOpen}>
            <CardBody>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <HistoryCharts
                    tooltipLabels={getToolTipLabels()}
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </ErrorBoundary>
            </CardBody>
          </Collapse>
        </Card>
        <Card>
          <CardHeader>
            <Row>
              <Col>
                <CardTitle tag="h4">
                  Current Vulnerabilities&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() =>
                      setCurrentVulnChartsOpen(!currentVulnChartsOpen)
                    }
                  >
                    <i
                      className={
                        currentVulnChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={currentVulnChartsOpen}>
                  <div className="d-inline float-right">
                    <IconButton
                      id="chartTypeButton"
                      iconClass={
                        currentChartType === ChartType.Bar
                          ? 'fa fa-chart-pie fa-lg'
                          : 'fa fa-chart-bar fa-lg'
                      }
                      tooltip={
                        currentChartType === ChartType.Bar
                          ? 'Display Pie Charts'
                          : 'Display Bar Charts'
                      }
                      onClick={() => {
                        setCurrentChartType(
                          currentChartType === ChartType.Bar
                            ? ChartType.Doughnut
                            : ChartType.Bar,
                        );
                      }}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={currentVulnChartsOpen}>
            <CardBody>
              <div className="row">
                <Suspense fallback={<LoadingScreen />}>
                  <CurrentCharts
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </div>
            </CardBody>
          </Collapse>
        </Card>
        <VulnerabilityMetrics />
      </div>
      <div ref={listingScrollRef} aria-hidden></div>
      <ErrorBoundary>
        <WAList />
      </ErrorBoundary>
      <ModalDialog
        title={modalComponentData?.title}
        subTitle={modalComponentData?.subTitle}
        childComponent={modalComponentData?.bodyComponent}
        onClose={() => {
          setModalComponentData(undefined);
          setIsGroupingByQidVulnListingOpen(false);
        }}
      />
    </Fragment>
  );
};

export default WADashboard;
